import { InformationCircleIcon, LockClosedIcon, XCircleIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { InputType, MIN_CARD_DIGITS } from 'config'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createPhoneLoanPayment,
  getPhonePaymentDefaults,
  getPhonePaymentQuote,
  IPhonePaymentDefaults,
  lookupPostalCode,
} from 'services'
import { ButtonGroup, Modal } from 'stories/components'
import { getItemsFromFullAddress, getPrice2decimal, InputValidate, validateEmail } from 'utils'
import { RenderInput } from 'utils/RenderInput'

export const PhonePaymentMethod = {
  card: 'card',
  us_bank_account: 'us_bank_account',
} as const

const methodTitles: Record<string, string> = {
  card: 'Credit / Debit Card',
  us_bank_account: 'ACH (Bank Account)',
}

const cardInputs = (): Record<string, InputType> => ({
  cardNumber: {
    inputType: 'text',
    title: 'Card Number',
    required: true,
    span: 2,
  },
  expDate: {
    inputType: 'text',
    title: 'Expiration (MM/YY)',
    required: true,
    span: 1,
  },
  cvv: {
    inputType: 'text',
    title: 'CVV',
    required: true,
    span: 1,
  },
})

/** Seeded from the bank account the loan is already enrolled with, when it has one. */
const achInputs = (bank?: IPhonePaymentDefaults['bank']): Record<string, InputType> => ({
  routingNumber: {
    type: 'routingNo',
    inputType: 'text',
    title: 'Routing Number',
    required: true,
    span: 2,
    value: bank?.routingNumber || '',
  },
  accountNumber: {
    type: 'bankAccountNo',
    inputType: 'text',
    title: 'Account Number',
    required: true,
    span: 2,
    value: bank?.accountNumber || '',
  },
  bankName: {
    inputType: 'text',
    title: 'Bank Name',
    tooltip: 'Fills itself in from the routing number. Type over it to use a name of your own.',
    span: 2,
    value: bank?.bankName || '',
  },
  accountType: {
    inputType: 'select',
    title: 'Account Type',
    options: { checking: 'Checking', savings: 'Savings' },
    value: bank?.accountType || 'checking',
    required: true,
    span: 2,
  },
})

/**
 * Only the payer's name is required here. The gateway takes a charge without a billing address, and
 * a staff member on the phone often has nothing to read back - so the address helps the charge (a
 * card issuer can decline on a failed AVS check) without standing in the way of taking the payment.
 */
const billingInputs = (): Record<string, InputType> => ({
  firstName: { inputType: 'text', title: 'First Name', required: true, span: 2 },
  lastName: { inputType: 'text', title: 'Last Name', required: true, span: 2 },
  addressLookup: {
    inputType: 'map',
    title: 'Address Lookup',
    tooltip: 'Find an address to fill in the fields below. This box itself is not sent anywhere.',
    span: 4,
  },
  address1: { inputType: 'text', title: 'Address', span: 4 },
  address2: { inputType: 'text', title: 'Address 2', span: 4 },
  city: { inputType: 'text', title: 'City', span: 2 },
  state: { inputType: 'text', title: 'State', span: 1 },
  zip: { inputType: 'text', title: 'Zip', span: 1 },
  email: {
    inputType: 'text',
    title: 'Email for the receipt',
    tooltip: 'Separate several addresses with commas. Every address listed gets the receipt.',
    span: 4,
  },
})

/** The lookup box fills the other fields in; it is not a billing field itself. */
const NOT_SENT_TO_GATEWAY = ['addressLookup']

/** Commas and semicolons both separate a list of receipt addresses, as they do on the backend. */
const receiptEmails = (value: any): string[] =>
  String(value ?? '')
    .split(/[,;]/)
    .map((item) => item.trim())
    .filter((item) => !!item)

/**
 * Takes a payment over the phone: staff key in the card or bank details the borrower reads out, we
 * charge them through the payment gateway, and on success the payment is applied to the loan with
 * the allocation already set up behind this modal.
 *
 * The card / bank numbers are posted straight through to the gateway and are never stored by us —
 * this component holds them in local state only for as long as the modal is open.
 */
export const PhonePaymentModal = ({
  amount,
  payment,
  defaultPayor,
  onClose,
  onDone,
}: {
  /** Amount to apply to the loan (Amount Paid on the payment behind this modal). */
  amount: number
  /** The allocation to post once the charge succeeds. */
  payment: Record<string, any>
  /** Borrower name to seed the billing fields with, when known. */
  defaultPayor?: string
  onClose: () => void
  /** Called with the backend result after the payment posts to the ledger. */
  onDone: (result: Record<string, any>) => void
}) => {
  const [method, setMethod] = useState<string>(PhonePaymentMethod.card)
  const [inputs, setInputs] = useState<Record<string, InputType>>({ ...cardInputs(), ...billingInputs() })
  const [fee, setFee] = useState<number | null>(null)
  const [feeNote, setFeeNote] = useState('')
  // The gateway product the fee was quoted under, which the charge then has to run through. It is
  // not always the button staff pressed: a debit card at a tenant with pinless debit enabled is
  // priced against USIO's pinless schedule, so quoting one product and charging the other would
  // declare a fee the charge's own program does not run.
  const [chargeMethod, setChargeMethod] = useState<string>(PhonePaymentMethod.card)
  const [isPinless, setPinless] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { fillBankNameInput, markBankNameFilled } = useBankNameLookup()
  /** The loan's own ACH account and borrower details, once they have been read. */
  const defaultsRef = useRef<IPhonePaymentDefaults | null>(null)
  const [bankOnFile, setBankOnFile] = useState('')
  /** The city and state this screen filled in from a ZIP, so re-typing the ZIP replaces them. */
  const autoFilled = useRef({ city: '', state: '' })
  /** Set once staff touch anything, so arriving defaults never pull the screen out from under them. */
  const touched = useRef(false)

  // Swap the payment-detail fields when the method changes, keeping whatever billing details the
  // user has already typed — those apply to both methods.
  useEffect(() => {
    setInputs((prev) => {
      const billing: Record<string, InputType> = {}
      Object.keys(billingInputs()).forEach((key) => {
        billing[key] = prev[key] ? cloneDeep(prev[key]) : billingInputs()[key]
      })
      return {
        ...(method === PhonePaymentMethod.card ? cardInputs() : achInputs(defaultsRef.current?.bank)),
        ...billing,
      }
    })
    // The ACH fields are rebuilt from the loan's account, so the bank name now sitting in them is
    // one this screen filled in - not one the user typed - and the directory may replace it.
    if (method === PhonePaymentMethod.us_bank_account) markBankNameFilled(defaultsRef.current?.bank?.bankName)
    setError('')
  }, [method])

  /**
   * Fill the screen in from the loan before staff type anything: the bank account it is already
   * enrolled with on its ACH tab, and the borrower's name, address and email.
   *
   * A payment taken over the phone is nearly always drawn on the account already on file, and
   * having the borrower read a routing number back digit by digit is slower and a chance to mis-key
   * it. Nothing already on screen is overwritten, and a loan with an account on file opens on ACH.
   */
  useEffect(() => {
    let cancelled = false
    getPhonePaymentDefaults()
      .then(async (res) => {
        if (cancelled || !res) return
        defaultsRef.current = res
        setBankOnFile(res.bank ? `${res.bank.bankName || 'Account'} ****${res.bank.accountNumber.slice(-4)}` : '')

        // The loan holds one formatted address line; the gateway wants it in pieces.
        const parts = res.payor?.address ? await getItemsFromFullAddress(res.payor.address).catch(() => null) : null
        if (cancelled) return

        setInputs((prev) => {
          const next = cloneDeep(prev)
          const seeded: Record<string, string> = {}
          const seed = (key: string, value?: string) => {
            if (next[key] && value && !`${next[key].value ?? ''}`.trim()) {
              next[key].value = value
              seeded[key] = value
            }
          }
          seed('firstName', res.payor?.firstName)
          seed('lastName', res.payor?.lastName)
          seed('email', res.payor?.email)
          seed('addressLookup', res.payor?.address)
          seed('address1', parts?.street_address1)
          seed('city', parts?.city)
          seed('state', parts?.state ? String(parts.state).slice(0, 2) : '')
          seed('zip', parts?.postal_code)
          // Only reached when ACH is already the selected method; the switch below seeds the rest.
          seed('routingNumber', res.bank?.routingNumber)
          seed('accountNumber', res.bank?.accountNumber)
          seed('bankName', res.bank?.bankName)
          // Everything above was filled in by this screen, so the lookups are free to replace it:
          // correcting the ZIP takes the town with it, and a different routing number brings its own
          // bank's name. Without this the guards cannot tell a prefilled value from a typed one and
          // stay silent on exactly the loans that opened with details on file.
          autoFilled.current = { city: seeded.city || '', state: seeded.state || '' }
          if (seeded.bankName) markBankNameFilled(seeded.bankName)
          return next
        })

        if (res.bank && !touched.current) setMethod(PhonePaymentMethod.us_bank_account)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  // Seed the name fields from the borrower on the loan, so staff only confirm them.
  useEffect(() => {
    if (!defaultPayor) return
    const [first, ...rest] = defaultPayor.trim().split(' ')
    setInputs((prev) => {
      if (prev.firstName?.value || prev.lastName?.value) return prev
      const next = cloneDeep(prev)
      next.firstName.value = first || ''
      next.lastName.value = rest.join(' ')
      return next
    })
  }, [defaultPayor])

  // A card convenience fee is priced by the gateway from the card number — it tells a debit card
  // from a credit card and charges each at its own rate — so the fee is unknowable until the card
  // is keyed in.
  //
  // Send the WHOLE number, not the first six digits. A six-digit prefix does not fail: USIO
  // answers success with a 0.00 fee, so this screen quoted the borrower no convenience fee at all
  // while the charge, which re-quotes with the full number, priced the real one.
  const cardNumberForFee = useMemo(() => {
    if (method !== PhonePaymentMethod.card) return ''
    const digits = String(inputs.cardNumber?.value || '').replace(/\D/g, '')
    return digits.length >= MIN_CARD_DIGITS ? digits : ''
  }, [method, inputs.cardNumber?.value])

  const needsCardForFee = method === PhonePaymentMethod.card && !cardNumberForFee

  // The gateway adds its own convenience fee on top of the amount applied to the loan; quote it so
  // staff can tell the borrower the total before the card is charged.
  useEffect(() => {
    let cancelled = false
    setFee(null)
    setFeeNote('')
    setChargeMethod(method)
    setPinless(false)
    if (!(amount > 0)) return
    if (needsCardForFee) {
      setFeeNote('Enter the full card number to quote the fee')
      return
    }
    getPhonePaymentQuote(amount, method, cardNumberForFee || undefined)
      .then((res: any) => {
        if (cancelled) return
        // `ok: false` means the gateway could not price it at all. That is not a $0 fee, and
        // showing it as one would have staff quote a total the charge then refuses.
        if (res?.available && res?.ok === false) {
          setFeeNote(res?.message || 'The convenience fee could not be quoted')
          return
        }
        setChargeMethod(res?.method || method)
        setPinless(!!res?.pinless)
        setFee(Number(res?.fee) || 0)
      })
      .catch(() => {
        if (!cancelled) setFeeNote('The convenience fee could not be quoted')
      })
    return () => {
      cancelled = true
    }
  }, [amount, method, cardNumberForFee, needsCardForFee])

  const total = useMemo(() => Number((amount + (fee || 0)).toFixed(2)), [amount, fee])

  /** One picked address fills the gateway's separate address fields. */
  const applyAddressLookup = async (fullAddress: string) => {
    const parts = fullAddress ? await getItemsFromFullAddress(fullAddress).catch(() => null) : null
    if (!parts) return
    setInputs((prev) => {
      const next = cloneDeep(prev)
      const fill = (key: string, value?: string) => {
        if (!next[key] || !value) return
        next[key].value = value
        next[key].error = ''
      }
      fill('address1', parts.street_address1)
      fill('city', parts.city)
      fill('state', parts.state ? String(parts.state).slice(0, 2) : '')
      fill('zip', parts.postal_code)
      autoFilled.current = { city: parts.city || '', state: parts.state ? String(parts.state).slice(0, 2) : '' }
      return next
    })
  }

  /**
   * The ZIP is the one piece of an address a payer always knows, so it fills in the town for them.
   * A city or state typed by hand is left alone; one this screen filled in itself is replaced, so
   * correcting the ZIP does not leave the wrong town sitting next to it.
   */
  const fillCityStateFromZip = async (current: Record<string, InputType>) => {
    const zip = String(current.zip?.value || '').replace(/\D/g, '')
    if (zip.length !== 5) return
    const city = `${current.city?.value ?? ''}`.trim()
    const state = `${current.state?.value ?? ''}`.trim()
    if (city && city !== autoFilled.current.city) return

    const found = await lookupPostalCode(zip).catch(() => null)
    if (!found?.city) return
    setInputs((prev) => {
      const next = cloneDeep(prev)
      next.city.value = found.city
      next.city.error = ''
      if (found.state && (!state || state === autoFilled.current.state)) {
        next.state.value = found.state
        next.state.error = ''
      }
      autoFilled.current = { city: found.city, state: found.state || autoFilled.current.state }
      return next
    })
  }

  const onChange = (key: string, value: any) => {
    touched.current = true
    const next = cloneDeep(inputs)
    next[key].value = value
    next[key].error = ''
    setInputs(next)
    setError('')

    if (key === 'routingNumber') fillBankNameInput(next, key, 'bankName', setInputs)
    if (key === 'zip') fillCityStateFromZip(next)
    if (key === 'addressLookup') applyAddressLookup(value)
  }

  /**
   * The backend mails the receipt itself and reports whether it went out, so a receipt that did not
   * send is said out loud while the borrower is still on the phone. It used to be the gateway's own
   * silent business, which is how a borrower ended up promised a receipt that never existed.
   *
   * A blank address is not a failure: staff who skipped the field already know no receipt is coming.
   */
  const reportReceipt = (receipt?: { sent?: boolean; to?: string; message?: string }) => {
    if (!receipt || receipt.sent || !receipt.to) return
    toast(receipt.message || `The receipt could not be emailed to ${receipt.to}.`, {
      type: 'warning',
      autoClose: false,
    })
  }

  const onSubmit = async () => {
    const next = cloneDeep(inputs)
    let hasError = false
    Object.keys(next).forEach((key) => {
      next[key].error = InputValidate({ ...next[key], value: next[key].value })
      if (next[key].error?.length) hasError = true
    })

    // The receipt field holds a list, so it is checked here rather than by the single-address rule.
    const badEmail = receiptEmails(next.email?.value).find((email) => !validateEmail(email))
    if (badEmail) {
      next.email.error = `"${badEmail}" is not a valid email address.`
      hasError = true
    }

    setInputs(next)
    if (hasError) return

    const values: Record<string, any> = {}
    Object.keys(next)
      .filter((key) => !NOT_SENT_TO_GATEWAY.includes(key))
      .forEach((key) => (values[key] = next[key].value))

    setLoading(true)
    setError('')
    try {
      const res: any = await createPhoneLoanPayment({
        ...values,
        // The product the quote resolved to, so the charge runs on the same fee schedule staff just
        // read out. The backend re-resolves it anyway and does not trust this.
        method: chargeMethod,
        amount,
        payment,
      })
      if (!res?.success) {
        setError(res?.message || 'The payment could not be completed.')
        // A charge that went through but failed to post is not a retry situation — the money has
        // moved, so surface it loudly and let the caller refresh.
        if (res?.charged) {
          toast(res.message, { type: 'warning', autoClose: false })
          reportReceipt(res.receipt)
          onDone(res)
        }
        return
      }
      toast(
        `Payment of $${getPrice2decimal(total, false, true)} accepted. Reference ${res.intentId}.${
          res.receipt?.sent ? ` Receipt emailed to ${res.receipt.to}.` : ''
        }`,
        { type: 'success' },
      )
      reportReceipt(res.receipt)
      onDone(res)
    } catch (e: any) {
      setError(e?.message || 'The payment could not be completed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Take Payment by Phone"
      isOpen
      onClose={onClose}
      onOk={onSubmit}
      titleOkay="Charge & Apply Payment"
      loading={isLoading}
      childLevel={1}
    >
      <div className="w-[720px] max-w-full pt-2">
        <div className="mb-4 flex items-start gap-2 rounded bg-blue-50 px-3 py-2 text-[13px] text-gray-700">
          <InformationCircleIcon className="mt-[2px] h-4 w-4 shrink-0 text-shade-blue" />
          <span>
            The card or bank details below are sent straight to the payment provider and are never stored here. Once the
            charge is accepted the payment is applied to the loan with the allocation you entered.
          </span>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-1 text-[14px]">
          <span>Amount applied to loan</span>
          <span className="text-right tabular-nums">${getPrice2decimal(amount, false, true)}</span>
          <span className="border-b border-gray-200 pb-1">Convenience fee</span>
          <span className="border-b border-gray-200 pb-1 text-right tabular-nums">
            {fee === null ? (
              <span className={feeNote && !needsCardForFee ? 'text-red-600' : 'text-gray-500'}>{feeNote || '—'}</span>
            ) : (
              `$${getPrice2decimal(fee, false, true)}`
            )}
          </span>
          <span className="font-semibold">Total charged to payer</span>
          <span className="text-right font-semibold tabular-nums">
            {fee === null ? '—' : `$${getPrice2decimal(total, false, true)}`}
          </span>
        </div>

        {isPinless && (
          <p className="-mt-2 mb-4 text-[12px] text-gray-500">
            This card runs as pinless debit, which is priced on its own fee schedule.
          </p>
        )}

        <div className="mb-4 flex items-center gap-3">
          <p className="text-[14px]">Payment Type:</p>
          <ButtonGroup
            title={methodTitles}
            value={method}
            onChange={(v: string) => {
              touched.current = true
              setMethod(v)
            }}
          />
        </div>

        {!!bankOnFile && method === PhonePaymentMethod.us_bank_account && (
          <p className="-mt-2 mb-4 text-[12px] text-gray-500">
            Filled in from the account on this loan's ACH tab ({bankOnFile}). Type over it to charge a different
            account.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {Object.keys(inputs).map((key) => (
            <div
              className={
                inputs[key].span === 4
                  ? 'col-span-1 sm:col-span-4'
                  : inputs[key].span === 3
                  ? 'col-span-1 sm:col-span-3'
                  : inputs[key].span === 2
                  ? 'col-span-1 sm:col-span-2'
                  : 'col-span-1'
              }
              key={key}
            >
              <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
            </div>
          ))}
        </div>

        {!!error && (
          <div className="mt-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[14px] text-red-700">
            <XCircleIcon className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="mt-4 flex items-center gap-1 text-[12px] text-gray-500">
          <LockClosedIcon className="h-3.5 w-3.5" />
          Card and bank numbers are not saved, logged, or shown again after this charge.
        </p>
      </div>
    </Modal>
  )
}
